// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { COMPUTE_RESOURCE_HA_STATUS } from 'admin/computeResource/constants';
import { Translate } from '@plesk/ui-library';
import {
    HaStatusContainer,
    HaStatusLabelContainer,
    IconContainer,
} from 'admin/computeResource/common/HaStatus/Styles';
import { ReactComponent as Shield } from 'common/icons/shield.svg';
import { ReactComponent as ShieldFilled } from 'common/icons/shield-filled.svg';
import { ReactComponent as ShieldNotEligible } from 'common/icons/shield-not-eligible.svg';
import { ReactComponent as ShieldPaused } from 'common/icons/shield-paused.svg';
import { ReactComponent as ShieldAttentionFilled } from 'common/icons/shield-attention-filled.svg';
import { ReactComponent as ShieldUnavailableFilled } from 'common/icons/shield-unavailable-filled.svg';
import { ReactComponent as ShieldError } from 'common/icons/shield-error.svg';
import { COLORS } from 'common/theme';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { useIsDown } from 'common/hooks/useBreakpoint';
import { BREAKPOINTS } from 'common/styles/breakpoints';

export interface IHaStatusProps {
    status: COMPUTE_RESOURCE_HA_STATUS;
}

export const haStatusTranslationMap: Record<COMPUTE_RESOURCE_HA_STATUS, React.ReactNode> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: <Translate content="computeResource.haStatuses.protected" />,
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: <Translate content="computeResource.haStatuses.notEligible" />,
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: <Translate content="computeResource.haStatuses.eligible" />,
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: <Translate content="computeResource.haStatuses.paused" />,
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: <Translate content="computeResource.haStatuses.atRisk" />,
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: <Translate content="computeResource.haStatuses.unavailable" />,
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: <Translate content="computeResource.haStatuses.notProtected" />,
};

const HA_STATUS_ICONS: Record<COMPUTE_RESOURCE_HA_STATUS, React.FC> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: () => <ShieldFilled color={COLORS.SUCCESS} />,
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: () => <ShieldNotEligible color={COLORS.GREY_60} />,
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: () => <Shield color={COLORS.GREY_60} />,
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: () => <ShieldPaused color={COLORS.GREY_60} />,
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: () => <ShieldAttentionFilled color={COLORS.WARNING} />,
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: () => <ShieldUnavailableFilled color={COLORS.GREY_60} />,
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: () => <ShieldError color={COLORS.DANGER} />,
};

export const HaStatus: React.FC<IHaStatusProps> = ({ status }) => {
    const isCompact = useIsDown(BREAKPOINTS.LG);

    if (!haStatusTranslationMap.hasOwnProperty(status)) {
        return null;
    }

    const Icon = HA_STATUS_ICONS[status];

    return (
        <HaStatusContainer>
            <Tooltip shown={isCompact} title={haStatusTranslationMap[status]}>
                <IconContainer>
                    <Icon />
                </IconContainer>
            </Tooltip>

            <HaStatusLabelContainer>
                {haStatusTranslationMap[status]}
            </HaStatusLabelContainer>
        </HaStatusContainer>
    );
};
